import React from "react";
import {
  BarChart3,
  ClipboardCheck,
  AlertCircle,
  Wrench,
  MessageSquare,
  FileText,
  ShieldCheck,
  Users,
  Image as ImageIcon,
  File as FileIcon,
} from "lucide-react";
import LoadingSpinner from "../../common/LoadingSpinner";

const GlobalAnalyticsView = ({
  kpis = {},
  charts = {},
  isLoading = false,
  error = null,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-800">
          Failed to load analytics. Please try again.
        </div>
      </div>
    );
  }

  const safeKpis = kpis || {};

  const inspections = safeKpis.inspections || {};
  const ncrs = safeKpis.ncrs || {};
  const rfis = safeKpis.rfis || {};
  const submittals = safeKpis.submittals || {};
  const materials = safeKpis.materials || {};
  const safety = safeKpis.safety || {};
  const findings = safeKpis.findings || {};
  const dailyReports = safeKpis.daily_reports || {};
  const users = safeKpis.users || {};
  const documents = safeKpis.documents || {};
  const photos = safeKpis.photos || {};

  const completionRate = Number(safeKpis.completion_rate ?? 0);
  const avgNcrResolution =
    Number(
      safeKpis.avg_ncr_resolution ??
        (safeKpis.ncrs && safeKpis.ncrs.avg_resolution_days) ??
        0,
    ) || 0;

  const testPassRate =
    Number(
      safeKpis.test_pass_rate ?? (materials && materials.pass_rate) ?? 0,
    ) || 0;

  const avgApprovalTimeDays = Number(safeKpis.avg_approval_time ?? 0) || 0;

  const avgRfiResponseDays =
    Number(safeKpis.avg_rfi_response ?? rfis.avg_response_days ?? 0) || 0;

  const avgSubmittalReviewDays =
    Number(safeKpis.avg_submittal_review ?? submittals.avg_review_days ?? 0) ||
    0;

  const performanceCards = [
    {
      label: "Inspection Completion",
      value: `${completionRate.toFixed(0)}%`,
      description: "Completed inspections vs total",
    },
    {
      label: "Avg NCR Resolution",
      value: `${avgNcrResolution.toFixed(1)} days`,
      description: "Issued → Closed",
    },
    {
      label: "Material Test Pass Rate",
      value: `${testPassRate.toFixed(1)}%`,
      description: "Pass vs fail material tests",
    },
    {
      label: "Avg RFI Response",
      value: `${avgRfiResponseDays.toFixed(1)} days`,
      description: "Submit → Answered",
    },
    {
      label: "Avg Submittal Review",
      value: `${avgSubmittalReviewDays.toFixed(1)} days`,
      description: "Submit → Reviewed",
    },
    {
      label: "Inspection Approval Time",
      value: `${avgApprovalTimeDays.toFixed(1)} days`,
      description: "Create → Approved",
    },
  ];

  const last7 = (charts && charts.inspections_last_7_days) || [];
  const last7Max = last7.length
    ? Math.max(1, ...last7.map((d) => d.count || 0))
    : 1;

  const ncrLast7 = (charts && charts.ncrs_last_7_days) || [];
  const ncrLast7Max = ncrLast7.length
    ? Math.max(1, ...ncrLast7.map((d) => d.count || 0))
    : 1;

  const statRow = (label, value, extraClass = "") => (
    <div className="flex items-center justify-between text-sm" key={label}>
      <span className="text-gray-500">{label}</span>
      <span className={`font-semibold text-gray-800 ${extraClass}`}>
        {value}
      </span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Global Analytics</h2>
          <p className="text-gray-600">
            Cross-project performance and SLA overview
          </p>
        </div>
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-orange-500" />
        </div>
      </div>

      {/* Performance overview */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
          <TrendingUpIcon className="w-4 h-4 mr-2" />
          Performance KPIs
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {performanceCards.map((card) => (
            <div
              key={card.label}
              className="border rounded-lg p-4 bg-gradient-to-br from-white to-gray-50 flex flex-col justify-between"
            >
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  {card.label}
                </p>
                <p className="mt-2 text-2xl font-bold text-gray-900">
                  {card.value}
                </p>
              </div>
              <p className="mt-2 text-xs text-gray-500">{card.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Operational analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inspections */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center">
              <ClipboardCheck className="w-4 h-4 mr-2 text-blue-500" />
              Inspections Analytics
            </h3>
            <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">
              Total: {inspections.total ?? 0}
            </span>
          </div>
          <div className="space-y-2">
            {statRow("Draft", inspections.draft ?? 0)}
            {statRow("Submitted", inspections.submitted ?? 0)}
            {statRow("Approved", inspections.approved ?? 0)}
            {statRow("Rejected", inspections.rejected ?? 0)}
            {statRow("Completed", inspections.completed ?? 0)}
            {statRow(
              "Critical inspections",
              inspections.critical ?? 0,
              "text-red-600",
            )}
          </div>

          {/* Inspections last 7 days micro chart */}
          <div className="mt-4">
            <p className="text-xs text-gray-500 mb-1">
              Inspections – last 7 days
            </p>
            {last7 && last7.length > 0 ? (
              <div className="flex items-end space-x-2 h-24">
                {last7.map((d) => (
                  <div
                    key={d.date}
                    className="flex-1 flex flex-col items-center"
                  >
                    <div
                      className="w-full rounded-t bg-blue-500 transition-all"
                      style={{
                        height: `${((d.count || 0) / last7Max) * 100}%`,
                      }}
                    />
                    <span className="mt-1 text-[10px] text-gray-500">
                      {d.date_label}
                    </span>
                    <span className="text-[10px] text-gray-700">
                      {d.count || 0}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400">No inspection data.</p>
            )}
          </div>
        </div>

        {/* NCRs */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center">
              <AlertCircle className="w-4 h-4 mr-2 text-red-500" />
              NCR Analytics
            </h3>
            <span className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-700">
              Total: {ncrs.total ?? 0}
            </span>
          </div>
          <div className="space-y-2">
            {statRow("Open", ncrs.open ?? 0, "text-orange-600")}
            {statRow("Closed", ncrs.closed ?? 0, "text-green-600")}
            {statRow("Overdue", ncrs.overdue ?? 0, "text-red-600")}
            {statRow(
              "Avg resolution time",
              `${(ncrs.avg_resolution_days ?? avgNcrResolution).toFixed?.(1) ?? avgNcrResolution.toFixed(1)} days`,
            )}
          </div>

          {/* NCR last 7 days micro chart */}
          <div className="mt-4">
            <p className="text-xs text-gray-500 mb-1">NCRs – last 7 days</p>
            {ncrLast7 && ncrLast7.length > 0 ? (
              <div className="flex items-end space-x-2 h-24">
                {ncrLast7.map((d) => (
                  <div
                    key={d.date}
                    className="flex-1 flex flex-col items-center"
                  >
                    <div
                      className="w-full rounded-t bg-red-500 transition-all"
                      style={{
                        height: `${((d.count || 0) / ncrLast7Max) * 100}%`,
                      }}
                    />
                    <span className="mt-1 text-[10px] text-gray-500">
                      {d.date_label}
                    </span>
                    <span className="text-[10px] text-gray-700">
                      {d.count || 0}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400">No NCR data.</p>
            )}
          </div>
        </div>
      </div>

      {/* RFIs + Submittals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* RFIs */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center">
              <MessageSquare className="w-4 h-4 mr-2 text-indigo-500" />
              RFI Analytics
            </h3>
            <span className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700">
              Total:{" "}
              {(rfis.total ?? (rfis.open ?? 0) + (rfis.closed ?? 0)) || 0}
            </span>
          </div>
          <div className="space-y-2">
            {statRow("Pending", rfis.pending ?? 0, "text-orange-600")}
            {statRow("Open", rfis.open ?? 0)}
            {statRow("Closed", rfis.closed ?? 0, "text-green-600")}
            {statRow("Overdue", rfis.overdue ?? 0, "text-red-600")}
            {statRow(
              "Avg response time",
              `${(rfis.avg_response_days ?? avgRfiResponseDays).toFixed?.(1) ?? avgRfiResponseDays.toFixed(1)} days`,
            )}
          </div>
        </div>

        {/* Submittals */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center">
              <FileText className="w-4 h-4 mr-2 text-emerald-500" />
              Submittal Analytics
            </h3>
            <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">
              Total: {submittals.total ?? 0}
            </span>
          </div>
          <div className="space-y-2">
            {statRow("Submitted", submittals.submitted ?? 0)}
            {statRow("Approved", submittals.approved ?? 0, "text-green-600")}
            {statRow("Rejected", submittals.rejected ?? 0, "text-red-600")}
            {statRow("Overdue", submittals.overdue ?? 0, "text-red-600")}
            {statRow(
              "Avg review time",
              `${(submittals.avg_review_days ?? avgSubmittalReviewDays).toFixed?.(1) ?? avgSubmittalReviewDays.toFixed(1)} days`,
            )}
          </div>
        </div>
      </div>

      {/* Materials, Safety, Findings, Reports, Users, Files */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Materials */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
            <Wrench className="w-4 h-4 mr-2 text-yellow-500" />
            Material Tests
          </h3>
          <div className="space-y-2">
            {statRow("Total tests", materials.total ?? 0)}
            {statRow(
              "Pass",
              materials.pass ?? materials.passed ?? 0,
              "text-green-600",
            )}
            {statRow(
              "Fail",
              materials.fail ?? materials.failed ?? 0,
              "text-red-600",
            )}
            {statRow(
              "Pass rate",
              `${(materials.pass_rate ?? testPassRate).toFixed?.(1) ?? testPassRate.toFixed(1)}%`,
            )}
          </div>
        </div>

        {/* Safety + Findings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
            <ShieldCheck className="w-4 h-4 mr-2 text-teal-500" />
            Safety & Findings
          </h3>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Safety Observations</p>
              <div className="space-y-1">
                {statRow("Total", safety.total ?? 0)}
                {statRow("Open", safety.open ?? 0, "text-orange-600")}
                {statRow("Closed", safety.closed ?? 0, "text-green-600")}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Inspection Findings</p>
              <div className="space-y-1">
                {statRow("Total", findings.total ?? 0)}
                {statRow("Critical", findings.critical ?? 0, "text-red-600")}
                {statRow("Open", findings.open ?? 0, "text-orange-600")}
              </div>
            </div>
          </div>
        </div>

        {/* Reports + Users + Files */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
            <Users className="w-4 h-4 mr-2 text-purple-500" />
            Usage & Artifacts
          </h3>
          <div className="space-y-3">
            <div className="space-y-1">
              <p className="text-xs text-gray-500 mb-1">Users</p>
              {statRow("Total users", users.total ?? 0)}
              {statRow("Active users", users.active ?? 0, "text-green-600")}
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500 mb-1">Daily Reports</p>
              {statRow("Total daily reports", dailyReports.total ?? 0)}
            </div>
            <div className="space-y-1">
              <p className="text-xs text-gray-500 mb-1">Files</p>
              {statRow(
                "Documents",
                documents.total ?? 0,
                "flex items-center gap-1",
              )}
              {statRow("Photos", photos.total ?? 0, "flex items-center gap-1")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// small internal icon so we don't have to import more from lucide-react
const TrendingUpIcon = (props) => (
  <svg
    {...props}
    viewBox="0 0 24 24"
    className={`inline-block ${props.className || ""}`}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

export default GlobalAnalyticsView;
